/**
 * 安全规则 —— 对应技术设计 §14.3「安全规则要内建在引擎里，而不是只在文字里提醒」。
 *
 * 【§22 红线 5】「安全错误必须可识别、可解释、可记录」——
 * 三件事缺一不可，所以每条安全事件必须同时带：
 *   识别 → `code`（可分类、可统计）
 *   解释 → `knowledgeId`（现场给出为什么）
 *   记录 → 进 `SafetyLog`（供 M1d 的过程报告结算）
 *
 * 安全规则**复用 rule-engine 的规则格式**，只是多一个 `severity` 字段并单独走这条通道。
 * 不另起一套 DSL：多一套语法就多一份实验包作者要学的东西，也多一处可能漂移的判据。
 */

import type { CompiledRule } from "./rule-engine.js";
import type { EvalContext } from "./condition-evaluator.js";

export type SafetySeverity = "info" | "warn" | "danger";

export interface SafetyEvent {
  /** 规则 id 即分类码，可直接用于统计「这个班最常犯哪条安全错误」 */
  code: string;
  severity: SafetySeverity;
  /** 现场解释用的知识点 id（§14.3「错误即知识点」） */
  knowledgeId?: string;
  /** 相对毫秒（v1.4 §1.2：相对时间才能跨设备对齐） */
  at: number;
  stepId?: string;
}

export interface SafetyLog {
  events: SafetyEvent[];
  /** 各严重度计数，报告直接用 */
  counts: Record<SafetySeverity, number>;
}

export function createSafetyLog(): SafetyLog {
  return { events: [], counts: { info: 0, warn: 0, danger: 0 } };
}

/** 只挑出带 severity 的规则 —— 它们就是安全规则。 */
export function selectSafetyRules(rules: readonly CompiledRule[]): CompiledRule[] {
  return rules.filter((rule) => rule.definition.severity !== undefined);
}

/**
 * 检查安全规则。返回本轮新触发的事件。
 *
 * `alreadyFired` 用来抑制重复告警：同一条安全规则在同一个步骤里只报一次，
 * 否则每帧都弹同一句提示，学生会直接忽略所有提示（§8「已掌握知识减少打扰」的同理）。
 */
export function checkSafety(
  safetyRules: readonly CompiledRule[],
  context: EvalContext,
  options: { at: number; stepId?: string; alreadyFired: ReadonlySet<string> },
): SafetyEvent[] {
  const fired: SafetyEvent[] = [];

  for (const rule of safetyRules) {
    const { definition } = rule;
    const key = `${definition.id}@${options.stepId ?? "-"}`;
    if (options.alreadyFired.has(key)) continue;
    if (definition.steps && options.stepId && !definition.steps.includes(options.stepId)) continue;
    if (!rule.matches(context)) continue;

    const knowledgeAction = definition.then.find((action) => "knowledge" in action);
    fired.push({
      code: definition.id,
      severity: definition.severity ?? "warn",
      ...(knowledgeAction && "knowledge" in knowledgeAction
        ? { knowledgeId: knowledgeAction.knowledge }
        : {}),
      at: options.at,
      ...(options.stepId !== undefined ? { stepId: options.stepId } : {}),
    });
  }

  return fired;
}

export function appendSafety(log: SafetyLog, events: readonly SafetyEvent[]): SafetyLog {
  if (events.length === 0) return log;
  const counts = { ...log.counts };
  for (const event of events) counts[event.severity] += 1;
  return { events: [...log.events, ...events], counts };
}

/**
 * 安全分：danger 扣得比 warn 重得多。满分 100，不设下限惩罚（不让学生因为
 * 一次探索性错误就彻底放弃，§14.1「错误可发生但要可恢复」）。
 */
export function safetyScore(log: SafetyLog): number {
  const penalty = log.counts.danger * 20 + log.counts.warn * 6 + log.counts.info * 0;
  return Math.max(0, 100 - penalty);
}
